/**
 * Highlight Service
 * Manages paper highlights with localStorage and backend sync
 */

import authService from "./authService";
import { buildApiPath } from "../utils/api";

const STORAGE_KEY = "pubmed-highlighted-papers";

class HighlightService {
  constructor() {
    this.highlights = this.loadFromLocalStorage();
    this.syncQueue = [];
    this.isSyncing = false;
  }

  /**
   * Load highlights from localStorage
   */
  loadFromLocalStorage() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error("Error loading highlights from localStorage:", error);
      return {};
    }
  }

  /**
   * Save highlights to localStorage
   */
  saveToLocalStorage() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.highlights));
    } catch (error) {
      console.error("Error saving highlights to localStorage:", error);
    }
  }

  /**
   * Get axios instance for API calls
   */
  getAxios() {
    return authService.getAxios();
  }

  /**
   * Check if a paper is highlighted
   */
  isHighlighted(searchId, pmid) {
    return this.highlights[searchId]?.[pmid] === true;
  }

  /**
   * Toggle highlight for a paper (optimistic update)
   */
  async toggleHighlight(searchId, pmid, paperTitle = "") {
    // Initialize search highlights if not exists
    if (!this.highlights[searchId]) {
      this.highlights[searchId] = {};
    }

    const wasHighlighted = this.highlights[searchId][pmid] === true;

    // Optimistic update - immediately update local state
    if (wasHighlighted) {
      delete this.highlights[searchId][pmid];
      // Clean up empty search entries
      if (Object.keys(this.highlights[searchId]).length === 0) {
        delete this.highlights[searchId];
      }
    } else {
      this.highlights[searchId][pmid] = true;
    }

    // Save to localStorage immediately
    this.saveToLocalStorage();

    // Sync with backend
    try {
      if (wasHighlighted) {
        await this.removeHighlight(searchId, pmid);
      } else {
        await this.addHighlight(searchId, pmid, paperTitle);
      }
    } catch (error) {
      // On error, revert the optimistic update
      console.error("Error syncing highlight with backend:", error);

      // Revert changes
      if (wasHighlighted) {
        if (!this.highlights[searchId]) {
          this.highlights[searchId] = {};
        }
        this.highlights[searchId][pmid] = true;
      } else {
        delete this.highlights[searchId]?.[pmid];
        if (
          this.highlights[searchId] &&
          Object.keys(this.highlights[searchId]).length === 0
        ) {
          delete this.highlights[searchId];
        }
      }

      this.saveToLocalStorage();
      throw error;
    }

    return !wasHighlighted;
  }

  /**
   * Add a highlight to the backend
   */
  async addHighlight(searchId, pmid, paperTitle) {
    try {
      const axios = this.getAxios();
      const response = await axios.post(buildApiPath("/highlights"), {
        search_id: searchId,
        pmid: pmid,
        paper_title: paperTitle,
      });
      return response.data;
    } catch (error) {
      console.error("Error adding highlight:", error);
      throw error;
    }
  }

  /**
   * Remove a highlight from the backend
   */
  async removeHighlight(searchId, pmid) {
    try {
      const axios = this.getAxios();
      const response = await axios.delete(
        buildApiPath(`/highlights/${searchId}/${pmid}`),
      );
      return response.data;
    } catch (error) {
      console.error("Error removing highlight:", error);
      throw error;
    }
  }

  /**
   * Get all highlights for a specific search
   */
  async getSearchHighlights(searchId) {
    try {
      const axios = this.getAxios();
      const response = await axios.get(buildApiPath(`/highlights/${searchId}`));

      // Update local cache
      if (!this.highlights[searchId]) {
        this.highlights[searchId] = {};
      }
      this.highlights[searchId] = response.data;
      this.saveToLocalStorage();

      return response.data;
    } catch (error) {
      console.error("Error fetching search highlights:", error);
      // Return local cache on error
      return this.highlights[searchId] || {};
    }
  }

  /**
   * Get all user highlights from backend
   */
  async getAllHighlights() {
    try {
      const axios = this.getAxios();
      const response = await axios.get(buildApiPath("/highlights"));

      // Rebuild local cache from backend data
      const newHighlights = {};
      response.data.forEach((highlight) => {
        const searchId = highlight.search_id;
        const pmid = highlight.pmid;

        if (!newHighlights[searchId]) {
          newHighlights[searchId] = {};
        }
        newHighlights[searchId][pmid] = true;
      });

      this.highlights = newHighlights;
      this.saveToLocalStorage();

      return response.data;
    } catch (error) {
      console.error("Error fetching all highlights:", error);
      return [];
    }
  }

  /**
   * Get highlights for multiple searches
   */
  getLocalHighlights(searchIds) {
    const result = {};
    searchIds.forEach((searchId) => {
      if (this.highlights[searchId]) {
        result[searchId] = this.highlights[searchId];
      }
    });
    return result;
  }

  /**
   * Clear all local highlights (for logout)
   */
  clearLocalHighlights() {
    this.highlights = {};
    this.saveToLocalStorage();
  }

  /**
   * Count total highlighted papers
   */
  getTotalHighlightCount() {
    let count = 0;
    Object.values(this.highlights).forEach((searchHighlights) => {
      count += Object.keys(searchHighlights).length;
    });
    return count;
  }

  /**
   * Get highlighted papers for a search
   */
  getHighlightedPapers(searchId, papers) {
    if (!papers || !Array.isArray(papers)) return [];

    const searchHighlights = this.highlights[searchId] || {};
    return papers.filter((paper) => {
      const pmid = paper.pmid || paper.PMID;
      return searchHighlights[pmid] === true;
    });
  }
}

// Create singleton instance
const highlightService = new HighlightService();

export default highlightService;
